import React, { useMemo } from "react";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useLocale } from "lib/hooks/useLocale";
import { CheckoutPlan } from "lib/types/subscriptions";
import { hexToRgba } from "lib/utils/helpers";
import { formatScheduleNote } from "lib/utils/subscriptions/planFormatters";

const FALLBACK_LINE_KEYS = [
  "subscription_schedule_first_order",
  "subscription_schedule_next_order",
  "subscription_schedule_ends",
  "subscription_schedule_delivery",
];

const PlanScheduleNote: React.FC<{ plan: CheckoutPlan }> = ({ plan }) => {
  const { t } = useLocale();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const config = checkoutMetadata?.uiAttributes?.subscriptionPlanConfig;
  const card = config?.subscriptionCard;
  // Matches PlanRow: fall back to the payment tile so an unstyled store stays consistent.
  const paymentCard = checkoutMetadata?.uiAttributes?.paymentTileConfig?.layoutUI?.paymentCard;
  const borderRadius = paymentCard?.borderRadius;

  const lines = useMemo(
    () => config?.summaryTitles ?? FALLBACK_LINE_KEYS.map((key) => t(key)),
    [config?.summaryTitles, t],
  );

  const notes = formatScheduleNote(plan, lines, t);

  if (!notes.length) return null;

  const textColor = card?.textColor || paymentCard?.textColor || undefined;

  return (
    <ul
      id="subscription-schedule-note"
      style={{
        ...(borderRadius === undefined ? {} : { borderRadius }),
        backgroundColor: hexToRgba((textColor ?? "#000000").substring(0, 7), 0.04, "rgba(0, 0, 0, 0.04)"),
      }}
      className="mt-1.5 flex flex-col gap-0.5 rounded-md px-3 py-4">
      {notes.map((note) => (
        <li key={note} className="flex items-start gap-2 text-text-xs-medium text-coal-medium">
          <span
            aria-hidden="true"
            className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-current opacity-50"
            style={textColor ? { backgroundColor: textColor, opacity: 0.4 } : undefined}
          />
          <span style={textColor ? { color: textColor, opacity: 0.6 } : undefined}>{note}</span>
        </li>
      ))}
    </ul>
  );
};

export default PlanScheduleNote;
